import clsx from "clsx";
import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { NavLink, useHistory } from "react-router-dom";

import { getDoctorsTasks, logoutUser } from "~/actions/dashboard";
import { PersonName } from "~/components/common/PersonName";
import { Badge } from "~/components/ui/badge";
import { useUpdateEffect } from "~/hooks/use-update-effect";
import { usePaymentsCount, useShipmentsCount, useUnpaidEventsCount } from "~/hooks/use-user";
import { useAppDispatch, useAppSelector } from "~/store";

import styles from "./profile_menu.module.css";

export function ProfileMenu() {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.user);
  const tour = useAppSelector((state) => state.tour);

  const numberOfUnpaidEvents = useUnpaidEventsCount();
  const numberOfPayments = usePaymentsCount();
  const numberOfShipments = useShipmentsCount();

  const doctorTasks = useAppSelector((state) => state.doctorTasks);
  const numberOfTasks = doctorTasks.patients.length;

  useEffect(() => {
    if (user.account_id && !doctorTasks.hasBeenLoaded) {
      dispatch(getDoctorsTasks());
    }
  }, [user.account_id, doctorTasks.hasBeenLoaded, dispatch]);

  useUpdateEffect(() => {
    if (!user.account_id) {
      history.replace("/login");
    }
  }, [user.account_id, history]);

  const has_signed_offer = user.has_signed_offer || intl.locale == "en";

  async function logout() {
    await dispatch(logoutUser());
    history.push("/login");
  }

  return (
    <div className="top-menu">
      <ul className="nav navbar-nav pull-right">
        <li className="separator hide"></li>

        {has_signed_offer ? (
          <>
            <ProfileMenuBell
              to="/pages/events"
              srOnlyText={<FormattedMessage id="events" />}
              title=""
              badgeCount={numberOfUnpaidEvents}
            >
              <AcademicCapIcon className="tw-text-[#c0cddc]" />
            </ProfileMenuBell>

            <ProfileMenuBell
              to="/pages/payments"
              srOnlyText={<FormattedMessage id="pat_table_tasks.block_payments" />}
              title={intl.formatMessage({ id: "tooltip_payments" })}
              badgeCount={numberOfPayments}
            >
              <WalletIcon className="tw-text-[#c0cddc]" />
            </ProfileMenuBell>

            <ProfileMenuBell
              to="/pages/tasks"
              srOnlyText={<FormattedMessage id="pat_table_tasks.block_head" />}
              title={intl.formatMessage({ id: "tooltip_notifications" })}
              badgeCount={numberOfTasks}
            >
              <BellIcon className="tw-text-[#c0cddc]" />
            </ProfileMenuBell>

            <ProfileMenuBell
              to="/pages/shipments"
              srOnlyText={<FormattedMessage id="pat.shipments.header" />}
              title={intl.formatMessage({ id: "tooltip_shipment" })}
              badgeCount={numberOfShipments}
            >
              <TruckIcon className="tw-text-[#c0cddc]" />
            </ProfileMenuBell>
          </>
        ) : null}

        <li className="dropdown dropdown-user">
          <button
            className={clsx("dropdown-toggle", styles.dropdownToggle, { disabled: tour })}
            data-toggle="dropdown"
            data-hover="dropdown"
            data-close-others="true"
            id="top-dd-menu"
            aria-label={intl.formatMessage({ id: "profile_menu" })}
            aria-controls="profile-menu-dropdown-menu"
          >
            <span className="hideOnMobile" id="current-user">
              <PersonName person={user} isDoctor />
            </span>
            <img alt="" className="img-circle" src="/img/avatars/no-avatar.svg" />
          </button>

          <ul id="profile-menu-dropdown-menu" className="dropdown-menu dropdown-menu-default">
            {has_signed_offer ? (
              <>
                <li>
                  <NavLink to="/pages/my_account" id="top-dd-menu-my-account">
                    <i aria-hidden className="icon-user" />
                    <FormattedMessage id="top.menu.profile" />
                  </NavLink>
                </li>

                <li>
                  <NavLink to="/pages/doctor/clinics" id="top-dd-menu-clinic-list">
                    <i aria-hidden className="icon-layers" />
                    <FormattedMessage id="top.menu.my_practices" />
                  </NavLink>
                </li>

                <li>
                  <NavLink to="/pages/doctor/clinic/create" id="top-dd-menu-clinic-create">
                    <i aria-hidden className="icon-plus" />
                    <FormattedMessage id="top.menu.add_practices" />
                  </NavLink>
                </li>

                <li className="divider"></li>
              </>
            ) : null}

            <li>
              <a onClick={logout} id="top-dd-menu-logout" href="#">
                <i aria-hidden className="icon-key" />
                <FormattedMessage id="top.menu.logout" />
              </a>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  );
}

function ProfileMenuBell({
  to,
  title,
  badgeCount,
  srOnlyText,
  children,
}: {
  to: string;
  title: string;
  badgeCount: number;
  srOnlyText: React.ReactChild;
  children?: React.ReactNode;
}) {
  return (
    <li className="dropdown">
      <NavLink
        to={to}
        title={title}
        className={clsx(
          "dropdown-toggle tw-relative",
          "tw-flex tw-h-[75px] tw-w-[60px] tw-items-center tw-justify-center tw-p-5",
          "sm:tw-w-[65px] desktop:tw-w-[75px]",
        )}
      >
        <span className="sr-only">{srOnlyText}</span>

        {children}

        {badgeCount > 0 ? (
          <Badge
            srOnlyText=""
            className="tw-absolute tw-right-[2px] tw-top-[15px] md:tw-right-[6px] md:tw-top-[11px]"
          >
            {badgeCount}
          </Badge>
        ) : null}
      </NavLink>
    </li>
  );
}

export function TruckIcon(props: React.SVGAttributes<SVGElement>) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M14 17.5V5.5C14 5.22386 13.7761 5 13.5 5H2.5C2.22386 5 2 5.22386 2 5.5V17.5C2 17.7761 2.22386 18 2.5 18H4M14 17.5C14 17.7761 13.7761 18 13.5 18H8M14 17.5V8.5C14 8.22386 14.2239 8 14.5 8H17.7929C17.9255 8 18.0527 8.05268 18.1464 8.14645L21.8536 11.8536C21.9473 11.9473 22 12.0745 22 12.2071V17.5C22 17.7761 21.7761 18 21.5 18H20M14 17.5C14 17.7761 14.2239 18 14.5 18H16M4 18C4 19.1046 4.89543 20 6 20C7.10457 20 8 19.1046 8 18M4 18C4 16.8954 4.89543 16 6 16C7.10457 16 8 16.8954 8 18M16 18C16 19.1046 16.8954 20 18 20C19.1046 20 20 19.1046 20 18M16 18C16 16.8954 16.8954 16 18 16C19.1046 16 20 16.8954 20 18"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function BellIcon(props: React.SVGAttributes<SVGElement>) {
  return (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M8.87541 17.7414C8.87541 17.7414 9.40663 20.7414 12.5366 20.7414C15.6666 20.7414 16.1978 17.7414 16.1978 17.7414M6.32713 9.2095C6.32713 5.78009 9.10722 3 12.5366 3C15.966 3 18.7461 5.78009 18.7461 9.2095V11.4962C18.7461 12.8972 19.1608 14.2669 19.938 15.4326L20.3723 16.0841C20.4468 16.1959 20.3947 16.3479 20.2672 16.3904C15.2492 18.0631 9.82404 18.0631 4.80602 16.3904C4.67859 16.3479 4.62643 16.1959 4.70094 16.0841L5.13526 15.4326C5.91242 14.2669 6.32713 12.8972 6.32713 11.4962V9.2095Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function WalletIcon(props: React.SVGAttributes<SVGElement>) {
  return (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M5.42533 19.782L5.76582 19.1138L5.76582 19.1138L5.42533 19.782ZM4.5513 18.908L5.21956 18.5675L5.21955 18.5675L4.5513 18.908ZM19.2413 19.782L18.9008 19.1138L18.9008 19.1138L19.2413 19.782ZM20.1153 18.908L19.4471 18.5675L19.4471 18.5675L20.1153 18.908ZM19.2413 6.21798L18.9008 6.88624L18.9008 6.88624L19.2413 6.21798ZM20.1153 7.09202L19.4471 7.43251L19.4471 7.43251L20.1153 7.09202ZM5.42533 6.21798L5.08484 5.54973L5.08484 5.54973L5.42533 6.21798ZM4.5513 7.09202L3.88305 6.75152L3.88305 6.75152L4.5513 7.09202ZM15.8793 14.891L16.2198 14.2227L15.8793 14.891ZM15.4423 14.454L16.1106 14.1135L15.4423 14.454ZM22.2243 14.454L21.5561 14.1135L22.2243 14.454ZM21.7873 14.891L21.4468 14.2227L21.7873 14.891ZM21.7873 11.109L21.4468 11.7772L21.7873 11.109ZM22.2243 11.546L21.5561 11.8865L22.2243 11.546ZM15.8793 11.109L16.2198 11.7772L15.8793 11.109ZM15.4423 11.546L16.1106 11.8865L15.4423 11.546ZM6.05477 5.30364C5.67018 5.45747 5.48312 5.89395 5.63696 6.27854C5.79079 6.66313 6.22727 6.85019 6.61186 6.69635L6.05477 5.30364ZM14.9619 2.54855L14.6834 1.8522L14.6834 1.8522L14.9619 2.54855ZM15.5833 6C15.5833 6.41421 15.9191 6.75 16.3333 6.75C16.7475 6.75 17.0833 6.41421 17.0833 6H15.5833ZM7.53331 6.75H17.1333V5.25H7.53331V6.75ZM17.1333 19.25H7.53331V20.75H17.1333V19.25ZM5.08331 16.8V9.2H3.58331V16.8H5.08331ZM19.5833 9.2V11.25H21.0833V9.2H19.5833ZM19.5833 15.1875V16.8H21.0833V15.1875H19.5833ZM7.53331 19.25C6.96088 19.25 6.57667 19.2494 6.28079 19.2252C5.99367 19.2018 5.85639 19.1599 5.76582 19.1138L5.08484 20.4503C5.4221 20.6221 5.77876 20.6892 6.15864 20.7203C6.52977 20.7506 6.98564 20.75 7.53331 20.75V19.25ZM3.58331 16.8C3.58331 17.3477 3.58273 17.8035 3.61305 18.1747C3.64409 18.5546 3.7112 18.9112 3.88305 19.2485L5.21955 18.5675C5.17341 18.4769 5.13153 18.3396 5.10807 18.0525C5.0839 17.7566 5.08331 17.3724 5.08331 16.8H3.58331ZM5.76582 19.1138C5.53062 18.9939 5.3394 18.8027 5.21956 18.5675L3.88304 19.2485C4.1467 19.7659 4.56739 20.1866 5.08484 20.4503L5.76582 19.1138ZM17.1333 20.75C17.681 20.75 18.1369 20.7506 18.508 20.7203C18.8879 20.6892 19.2445 20.6221 19.5818 20.4503L18.9008 19.1138C18.8102 19.1599 18.673 19.2018 18.3858 19.2252C18.09 19.2494 17.7057 19.25 17.1333 19.25V20.75ZM19.5833 16.8C19.5833 17.3724 19.5827 17.7566 19.5586 18.0525C19.5351 18.3396 19.4932 18.4769 19.4471 18.5675L20.7836 19.2485C20.9554 18.9112 21.0225 18.5546 21.0536 18.1747C21.0839 17.8035 21.0833 17.3477 21.0833 16.8H19.5833ZM19.5818 20.4503C20.0992 20.1866 20.5199 19.7659 20.7836 19.2485L19.4471 18.5675C19.3272 18.8027 19.136 18.9939 18.9008 19.1138L19.5818 20.4503ZM17.1333 6.75C17.7057 6.75 18.09 6.75058 18.3858 6.77475C18.673 6.79821 18.8102 6.8401 18.9008 6.88624L19.5818 5.54973C19.2445 5.37788 18.8879 5.31077 18.508 5.27974C18.1369 5.24941 17.681 5.25 17.1333 5.25V6.75ZM21.0833 9.2C21.0833 8.65232 21.0839 8.19646 21.0536 7.82533C21.0225 7.44544 20.9554 7.08878 20.7836 6.75152L19.4471 7.43251C19.4932 7.52307 19.5351 7.66035 19.5586 7.94747C19.5827 8.24335 19.5833 8.62757 19.5833 9.2H21.0833ZM18.9008 6.88624C19.136 7.00608 19.3272 7.19731 19.4471 7.43251L20.7836 6.75152C20.5199 6.23408 20.0992 5.81338 19.5818 5.54973L18.9008 6.88624ZM7.53331 5.25C6.98564 5.25 6.52977 5.24941 6.15864 5.27974C5.77876 5.31077 5.4221 5.37788 5.08484 5.54973L5.76582 6.88624C5.85639 6.8401 5.99367 6.79821 6.28079 6.77475C6.57667 6.75058 6.96088 6.75 7.53331 6.75V5.25ZM5.08331 9.2C5.08331 8.62757 5.0839 8.24335 5.10807 7.94747C5.13153 7.66035 5.17341 7.52307 5.21955 7.43251L3.88305 6.75152C3.7112 7.08878 3.64409 7.44544 3.61305 7.82533C3.58273 8.19646 3.58331 8.65232 3.58331 9.2H5.08331ZM5.08484 5.54973C4.56739 5.81338 4.1467 6.23408 3.88305 6.75152L5.21955 7.43251C5.3394 7.19731 5.53062 7.00608 5.76582 6.88624L5.08484 5.54973ZM16.9333 11.75H20.7333V10.25H16.9333V11.75ZM21.5833 12.6V13.4H23.0833V12.6H21.5833ZM20.7333 14.25H16.9333V15.75H20.7333V14.25ZM16.0833 13.4V12.6H14.5833V13.4H16.0833ZM16.9333 14.25C16.6409 14.25 16.4667 14.2494 16.3376 14.2389C16.2172 14.229 16.2034 14.2144 16.2198 14.2227L15.5388 15.5593C15.7691 15.6766 16.0023 15.7165 16.2154 15.7339C16.4198 15.7506 16.6657 15.75 16.9333 15.75V14.25ZM14.5833 13.4C14.5833 13.6676 14.5827 13.9135 14.5994 14.1179C14.6168 14.331 14.6567 14.5642 14.7741 14.7945L16.1106 14.1135C16.1189 14.1299 16.1043 14.1161 16.0944 13.9957C16.0839 13.8666 16.0833 13.6924 16.0833 13.4H14.5833ZM16.2198 14.2227C16.1728 14.1988 16.1345 14.1605 16.1106 14.1135L14.7741 14.7945C14.9418 15.1238 15.2095 15.3915 15.5388 15.5593L16.2198 14.2227ZM21.5833 13.4C21.5833 13.6924 21.5827 13.8666 21.5722 13.9957C21.5623 14.1161 21.5477 14.1299 21.5561 14.1135L22.8926 14.7945C23.0099 14.5642 23.0498 14.331 23.0672 14.1179C23.0839 13.9135 23.0833 13.6676 23.0833 13.4H21.5833ZM20.7333 15.75C21.001 15.75 21.2468 15.7506 21.4512 15.7339C21.6643 15.7165 21.8975 15.6766 22.1278 15.5593L21.4468 14.2227C21.4632 14.2144 21.4494 14.229 21.329 14.2389C21.1999 14.2494 21.0257 14.25 20.7333 14.25V15.75ZM21.5561 14.1135C21.5321 14.1605 21.4939 14.1988 21.4468 14.2227L22.1278 15.5593C22.4571 15.3915 22.7248 15.1238 22.8926 14.7945L21.5561 14.1135ZM20.7333 11.75C21.0257 11.75 21.1999 11.7506 21.329 11.7611C21.4494 11.771 21.4632 11.7856 21.4468 11.7772L22.1278 10.4407C21.8975 10.3234 21.6643 10.2835 21.4512 10.2661C21.2468 10.2494 21.001 10.25 20.7333 10.25V11.75ZM23.0833 12.6C23.0833 12.3323 23.0839 12.0865 23.0672 11.8821C23.0498 11.669 23.0099 11.4358 22.8926 11.2055L21.5561 11.8865C21.5477 11.8701 21.5623 11.8839 21.5722 12.0043C21.5827 12.1334 21.5833 12.3076 21.5833 12.6H23.0833ZM21.4468 11.7772C21.4939 11.8012 21.5321 11.8395 21.5561 11.8865L22.8926 11.2055C22.7248 10.8762 22.4571 10.6085 22.1278 10.4407L21.4468 11.7772ZM16.9333 10.25C16.6657 10.25 16.4198 10.2494 16.2154 10.2661C16.0023 10.2835 15.7691 10.3234 15.5388 10.4407L16.2198 11.7772C16.2034 11.7856 16.2172 11.771 16.3376 11.7611C16.4667 11.7506 16.6409 11.75 16.9333 11.75V10.25ZM16.0833 12.6C16.0833 12.3076 16.0839 12.1334 16.0944 12.0043C16.1043 11.8839 16.1189 11.8701 16.1106 11.8865L14.7741 11.2055C14.6567 11.4358 14.6168 11.669 14.5994 11.8821C14.5827 12.0865 14.5833 12.3323 14.5833 12.6H16.0833ZM15.5388 10.4407C15.2095 10.6085 14.9418 10.8762 14.7741 11.2055L16.1106 11.8865C16.1345 11.8395 16.1728 11.8012 16.2198 11.7772L15.5388 10.4407ZM6.61186 6.69635L15.2405 3.24491L14.6834 1.8522L6.05477 5.30364L6.61186 6.69635ZM15.5833 3.47703V6H17.0833V3.47703H15.5833ZM15.2405 3.24491C15.4047 3.17922 15.5833 3.30016 15.5833 3.47703H17.0833C17.0833 2.23897 15.8329 1.39239 14.6834 1.8522L15.2405 3.24491Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function AcademicCapIcon(props: React.SVGAttributes<SVGElement>) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M4.65337 10.754L2.25477 9.33015C1.91508 9.1285 1.91508 8.64142 2.25477 8.43977L10.9201 3.29594C11.5848 2.90135 12.4152 2.90135 13.0799 3.29594L21.7452 8.43977C22.0849 8.64142 22.0849 9.1285 21.7452 9.33015L19.3466 10.754M4.65337 10.754L10.92 14.474C11.5847 14.8686 12.4152 14.8686 13.0799 14.474L19.3466 10.754M4.65337 10.754V15.8083C4.65337 16.5377 5.04022 17.2137 5.67246 17.589L10.92 20.7041C11.5847 21.0986 12.4152 21.0986 13.0799 20.7041L18.3275 17.589C18.9598 17.2137 19.3466 16.5377 19.3466 15.8083V10.754M12 9H15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
